import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import RecipeEdit from './components/Recipe/RecipeEdit';
import RecipeNew from './components/Recipe/RecipeNew';
import RecipeGallery from './components/Recipe/RecipeGallery';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import VerifyEmail from './components/Auth/VerifyEmail';
import RegistrationSuccess from './components/Auth/RegistrationSuccess';
import AccountSettings from './components/Account/AccountSettings';
import VaultPage from './components/Vault/VaultPage';
import ProtectedRoute from './components/Navigation/ProtectedRoute';
import { NavigationProvider } from './contexts/NavigationContext';
import { AuthProvider } from './contexts/AuthContext';
import { RecipeUndoProvider } from './contexts/RecipeUndoContext';
import AdminRoute from './components/Navigation/AdminRoute';
import AdminPage from './components/Admin/AdminPage';
import VersionMonitor from './components/Version/VersionMonitor';
import SiteShell from './components/Navigation/SiteShell';
import { AppShellProvider } from './contexts/AppShellContext';
import { PrivacyPolicy, TermsOfService } from './components/Legal/LegalPage';
import RecipeSearchPage from './components/Recipe/RecipeSearchPage';

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <NavigationProvider>
          <AppShellProvider>
            <RecipeUndoProvider>
            <VersionMonitor />
            <div className="min-h-screen bg-gray-50 flex flex-col mx-auto">
              <main className="flex-1 flex overflow-hidden">
                <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />
                <Route path="/registration-success" element={<RegistrationSuccess />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />

                {/* Protected routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <SiteShell workspace><RecipeNew /></SiteShell>
                  </ProtectedRoute>
                } />
                <Route path="/gallery" element={
                  <ProtectedRoute>
                    <SiteShell workspace><RecipeGallery /></SiteShell>
                  </ProtectedRoute>
                } />
                <Route path="/recipes/:name/edit" element={
                  <ProtectedRoute>
                    <SiteShell workspace><RecipeEdit key="saved-recipe" /></SiteShell>
                  </ProtectedRoute>
                } />
                <Route path="/account" element={
                  <ProtectedRoute>
                    <SiteShell><AccountSettings /></SiteShell>
                  </ProtectedRoute>
                } />
                <Route path="/vault" element={
                  <ProtectedRoute>
                    <SiteShell><VaultPage /></SiteShell>
                  </ProtectedRoute>
                } />
                <Route path="/search" element={
                  <ProtectedRoute>
                    <SiteShell workspace><RecipeSearchPage /></SiteShell>
                  </ProtectedRoute>
                } />
                <Route path="/admin/*" element={
                  <AdminRoute>
                    <SiteShell><AdminPage /></SiteShell>
                  </AdminRoute>
                } />
                <Route path="/recipes/new" element={
                  <ProtectedRoute>
                    <SiteShell workspace><RecipeEdit key="new-recipe" newRecipe /></SiteShell>
                  </ProtectedRoute>
                } />
                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
            </div>
            </RecipeUndoProvider>
          </AppShellProvider>
        </NavigationProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
